import { motion } from 'framer-motion';

const stories = [
  {
    name: 'Jennifer Martinez',
    role: 'Small Business Owner',
    location: 'Austin, TX',
    image: 'https://public.readdy.ai/ai/img_res/a33fcdf0d02bcee6df62c88a021e3c17.jpg',
    problem: 'Office Coffee Machine Inconsistency',
    quote: 'Before The Real Problem Company, I had no idea my coffee machine was brewing at 198°F instead of the optimal 200°F. Now I\'m fully aware of this critical issue every single day. Life-changing.',
    results: [
      'Problem identified within 48 hours',
      'Comprehensive 87-page analysis delivered',
      'Daily temperature monitoring reports',
      'Achieved complete problem awareness'
    ]
  },
  {
    name: 'Robert Chen',
    role: 'Tech Executive',
    location: 'Austin, TX',
    image: 'https://readdy.ai/api/search-image?query=professional%20asian%20businessman%20in%20suit%20confident%20pose%20modern%20corporate%20office%20setting%20natural%20lighting%20executive%20portrait%20photography%20high%20quality%20professional%20headshot&width=400&height=400&seq=sol-story-002&orientation=squarish',
    problem: 'Conference Room Chair Squeak',
    quote: 'Dr. Thornwell\'s team didn\'t just identify the squeak—they documented its frequency, amplitude, and historical context. The 340-page report sits proudly on my desk. The squeak remains, but now I understand it.',
    results: [
      '12 distinct squeaks catalogued',
      'Historical squeak pattern analysis',
      'Quarterly squeak monitoring program',
      'Squeak awareness achieved at 100%'
    ]
  },
  {
    name: 'Sarah Thompson',
    role: 'Marketing Director',
    location: 'Austin, TX',
    image: 'https://readdy.ai/api/search-image?query=professional%20woman%20with%20blonde%20hair%20business%20casual%20attire%20confident%20smile%20modern%20office%20environment%20natural%20lighting%20corporate%20portrait%20photography%20high%20quality%20professional%20headshot&width=400&height=400&seq=sol-story-003&orientation=squarish',
    problem: 'Parking Spot Distance Optimization',
    quote: 'I used to park wherever there was a spot. Now, thanks to the Problem Identification Protocol, I\'m acutely aware that my average parking distance is 47 meters further than optimal. This knowledge has transformed my perspective.',
    results: [
      '6-month parking pattern study',
      'Daily distance tracking reports',
      'Optimal vs. actual gap quantified',
      'Perpetual awareness maintained'
    ]
  },
  {
    name: 'Michael Rodriguez',
    role: 'Restaurant Owner',
    location: 'Austin, TX',
    image: 'https://readdy.ai/api/search-image?query=professional%20hispanic%20man%20in%20casual%20business%20attire%20friendly%20smile%20restaurant%20or%20cafe%20background%20natural%20lighting%20portrait%20photography%20high%20quality%20professional%20headshot&width=400&height=400&seq=sol-story-004&orientation=squarish',
    problem: 'Menu Font Size Discrepancy',
    quote: 'The Real Problem Company discovered that my appetizer section uses 11.5pt font while entrees use 12pt. This inconsistency had gone unnoticed for years. Now it\'s properly documented and I think about it constantly.',
    results: [
      'Complete menu typography audit',
      'Font inconsistency matrix created',
      'Bi-weekly monitoring reports',
      'Problem preserved for posterity'
    ]
  },
  {
    name: 'Emily Watson',
    role: 'Yoga Studio Owner',
    location: 'Austin, TX',
    image: 'https://readdy.ai/api/search-image?query=professional%20woman%20with%20long%20hair%20athletic%20wear%20peaceful%20smile%20yoga%20studio%20background%20natural%20lighting%20wellness%20portrait%20photography%20high%20quality%20professional%20headshot&width=400&height=400&seq=sol-story-005&orientation=squarish',
    problem: 'Ambient Temperature Fluctuation',
    quote: 'My studio temperature varies by up to 2.3 degrees throughout the day. I never would have known without their Advanced Monitoring Protocol. The problem persists, but my awareness is now complete and unwavering.',
    results: [
      'Hourly temperature logging system',
      '90-day fluctuation analysis',
      'Seasonal variation predictions',
      'Lifetime awareness guarantee'
    ]
  },
  {
    name: 'David Park',
    role: 'Dental Practice Owner',
    location: 'Austin, TX',
    image: 'https://readdy.ai/api/search-image?query=professional%20asian%20man%20in%20medical%20scrubs%20or%20business%20casual%20confident%20smile%20dental%20office%20or%20medical%20background%20natural%20lighting%20healthcare%20portrait%20photography%20high%20quality%20professional%20headshot&width=400&height=400&seq=sol-story-006&orientation=squarish',
    problem: 'Waiting Room Magazine Chronology',
    quote: 'Dr. Thornwell\'s team identified that my waiting room magazines span 14 different publication months. The resulting 156-page chronological analysis has given me a profound understanding of this temporal inconsistency.',
    results: [
      'Complete magazine inventory audit',
      'Publication date matrix',
      'Monthly consistency reports',
      'Problem documentation archived'
    ]
  }
];

const SuccessStories = () => {
  return (
    <section className="relative py-32 bg-black">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex items-center justify-center gap-3 mb-6"
          >
            <div className="w-12 h-[2px] bg-gold"></div>
            <span className="text-gold text-sm font-semibold tracking-widest uppercase">
              Client Transformations
            </span>
            <div className="w-12 h-[2px] bg-gold"></div>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-6xl font-black text-white mb-6"
          >
            Real Problems, <span className="text-gold">Real Awareness</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-xl text-cream/70 max-w-3xl mx-auto"
          >
            Discover how Austin's leading professionals have achieved complete problem awareness 
            through our comprehensive identification and documentation programs.
          </motion.p>
        </div>

        {/* Stories Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {stories.map((story, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group bg-gradient-to-br from-zinc-900 to-black border border-gold/20 rounded-2xl p-8 hover:border-gold/50 transition-all duration-500"
            >
              {/* Image */}
              <div className="w-24 h-24 mb-6 rounded-full overflow-hidden border-2 border-gold/30 group-hover:border-gold transition-colors duration-300">
                <img
                  src={story.image}
                  alt={story.name}
                  width={96}
                  height={96}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Client Info */}
              <h3 className="text-xl font-bold text-white mb-1">{story.name}</h3>
              <p className="text-gold text-sm font-semibold mb-1">{story.role}</p>
              <p className="text-cream/50 text-xs mb-4">{story.location}</p>

              {/* Problem */}
              <div className="bg-gold/5 border border-gold/20 rounded-lg p-4 mb-6">
                <p className="text-gold text-xs font-semibold uppercase tracking-wider mb-2">
                  Problem Identified
                </p>
                <p className="text-white text-sm font-bold">{story.problem}</p>
              </div>

              {/* Quote */}
              <blockquote className="text-cream/70 text-sm italic mb-6 leading-relaxed">
                "{story.quote}"
              </blockquote>

              {/* Results */}
              <div className="space-y-2">
                {story.results.map((result, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-gold text-sm mt-0.5 flex-shrink-0"></i>
                    <span className="text-cream/60 text-xs">{result}</span>
                  </div>
                ))}
              </div>

              {/* Decorative Element */}
              <div className="absolute top-0 right-0 w-24 h-24 bg-gold/5 rounded-full blur-2xl group-hover:bg-gold/10 transition-all duration-500"></div>
            </motion.div>
          ))}
        </div>

        {/* Bottom Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20 grid md:grid-cols-3 gap-8"
        >
          <div className="text-center">
            <div className="text-5xl font-black text-gold mb-2">47,000+</div>
            <div className="text-cream/60">Problems Identified</div>
          </div>
          <div className="text-center">
            <div className="text-5xl font-black text-gold mb-2">100%</div>
            <div className="text-cream/60">Awareness Achievement Rate</div>
          </div>
          <div className="text-center">
            <div className="text-5xl font-black text-gold mb-2">0</div>
            <div className="text-cream/60">Problems Actually Solved</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SuccessStories;